import { captureError } from '../services/MonitoringService';

/**
 * Send high-severity security events to Sentry for centralized monitoring
 * @param eventType - Type of security event
 * @param userId - User ID associated with the event
 * @param details - Additional details about the event
 * @param status - Status of the event (success, failure, etc.)
 */
export const reportSecurityEvent = (eventType:  string,
  userId: string,
  details: Record<string, unknown> = {},
  status: 'success' | 'failure' | 'warning' = 'success'
): void => {
  // Only report failures and warnings to Sentry
  if (status === 'success') return;
  
  // Create a descriptive message for Sentry
  const message = `Security Event: ${eventType} | Status: ${status}`;
  
  // Determine severity based on event type and status
  const level = getSeverityLevel(eventType, status);
  
  // Send to Sentry with appropriate context
  captureError(message, {
    level,
    tags: {
      securityEvent: 'true',
      eventType,
      status
    },
    extra: details,
    user: {
      id: userId
    },
    component: 'SecuritySystem',
    action: eventType
  });
};

/**
 * Determine the Sentry severity level based on event type and status
 */
function getSeverityLevel(eventType:  string, status: string): 'fatal' | 'error' | 'warning' | 'info' {
  // Critical security events that should be treated as errors
  const criticalEvents = [
    'login', 
    'permission_change', 
    'role_change', 
    'encryption_disabled', 
    'suspicious_activity',
    'access_denied',
    'mass_deletion'
  ];
  
  // If it's a critical event and failed, it's a severe error
  if (criticalEvents.includes(eventType) && status === 'failure') {
    return 'error';
  }
  
  // If it's a critical event with a warning status, mark as warning
  if (criticalEvents.includes(eventType) && status === 'warning') {
    return 'warning';
  }
  
  // All other security events are warnings
  return status === 'failure' ? 'warning' : 'info';
}

/**
 * Report suspicious activity to Sentry
 * @param userId - User ID associated with the activity
 * @param activity - Description of the suspicious activity
 * @param details - Additional details about the activity
 */
export const reportSuspiciousActivity = (userId:  string,
  activity: string,
  details: Record<string, unknown> = {}
): void => {
  captureError(`Suspicious Activity: ${activity}`, {
    level: 'warning',
    tags: {
      securityEvent: 'true',
      eventType: 'suspicious_activity'
    },
    extra: {
      ...details,
      detectedAt: new Date().toISOString()
    },
    user: {
      id: userId
    },
    component: 'SecuritySystem',
    action: 'suspicious_activity_detected'
  });
};
